import DropdownOptionItem from "../ui/dropdown-option-item";
import DynamicIcon from "../ui/dynamic-icon";
import icon from "../ui/icon";
import SearchItem from "../ui/search-item";
import SearchModalSection from "../ui/search-modal-section";
import SearchModal from "./search-modal";
import { useRouter } from "next/router";
import { useContext, useState } from "react";
import { useTranslations } from "next-intl";
import BusinessContext from "../../contexts/business-context";
import TableContext from "../../contexts/table-context";
import CreateFolderModal from "../ui/create-folder-modal";
import SideBarContext from "../../contexts/side-bar-context";

const GeneralSearchModal = ({ toggleModal, isOpen }) => {
  const router = useRouter();
  const { activeBusiness } = useContext(BusinessContext);
  const { tables, createTable } = useContext(TableContext);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [query, setQuery] = useState('');
  const t = useTranslations('generalSearch');
  const { chats } = useContext(SideBarContext);

  const onSearchChange = (value) => {
    setQuery(value);
  }

  const closeModal = () => {
    setQuery('');
    toggleModal();
  }

  const toggleCreateFolder = () => {
    setIsCreateFolderOpen(prev => !prev);
  }

  const lowerQuery = query.toLowerCase().trim();

  const allNavigations = [
    { name: t('goToHome'),        url: `/${router.query.uuid}/home`,        keywords: ['home', 'inicio', 'accueil'], always: true },
    { name: t('goToSettings'),    url: `/${router.query.uuid}/manage-people`, keywords: ['settings', 'people', 'manage', 'team', 'config'], always: true },
    { name: t('goToChats'),       url: `/${router.query.uuid}/chats`,        keywords: ['chat', 'chats', 'message', 'talk'] },
    { name: t('goToTerms'),       url: `/terms-and-conditions`,              keywords: ['terms', 'conditions', 'legal', 'privacy'] },
    { name: t('createNewBusiness'), url: `/businesses/new`,                  keywords: ['business', 'new business', 'company', 'create business'] },
    { name: t('reportABug'),      url: "https://forms.gle/rqLWcSkrpiNouynD8", keywords: ['bug', 'report', 'issue', 'problem'], always: true },
  ];

  const navigations = allNavigations.filter(({ keywords, always }) =>
    always || (lowerQuery && keywords.some(kw => kw.startsWith(lowerQuery) || lowerQuery.startsWith(kw)))
  );

  const handlePromptClick = (prompt) => {
    const businessUuid = activeBusiness?.uuid || router.query.uuid;
    localStorage.setItem('pendingChatPrompt', prompt);
    router.push(`/${businessUuid}/chats/new`);
    closeModal();
  };

  const handleAskAi = (query) => {
    const businessUuid = activeBusiness?.uuid || router.query.uuid;
    localStorage.setItem('pendingChatPrompt', query);
    router.push(`/${businessUuid}/chats/new`);
    closeModal();
  };

  const onCreateFolder = (name, extraCallback) => {

    const callBack = () => {
      if (extraCallback) {
        extraCallback();
      }
    }

    createTable('folder', name, callBack, 'private');
  }

  const filteredTables = tables?.filter(table => table.name.toLowerCase().includes(query.toLowerCase()));
  const filteredChats = lowerQuery ? chats?.filter(chat => chat.title?.toLowerCase().includes(lowerQuery)) : chats;

  // Table types that can be created, with their keyword aliases for matching
  const tableTypeActions = [
    { type: 'document', keywords: ['document', 'page', 'doc', 'note'], label: t('newPage'), icon: 'file' },
    { type: 'kanban',   keywords: ['kanban', 'board', 'task'],         label: t('newKanban'), icon: 'table' },
    { type: 'blank',    keywords: ['blank', 'table', 'grid'],          label: t('newBlankTable'), icon: 'table' },
    { type: 'okrs',     keywords: ['okr', 'okrs', 'objective', 'kpi'], label: t('newOkrs'), icon: 'list-collapse' },
  ];

  // "page" (document) is always shown; other types only when their keyword is matched.
  const visibleTypeActions = tableTypeActions.filter(({ type, keywords }) =>
    type === 'document' || (lowerQuery && keywords.some(kw => kw.startsWith(lowerQuery) || lowerQuery.startsWith(kw)))
  );

  const [showAllTables, setShowAllTables] = useState(false);
  const maxVisibleTables = 4;
  const hasMoreTables = filteredTables?.length > maxVisibleTables;
  const displayedTables = showAllTables ? filteredTables : filteredTables?.slice(0, maxVisibleTables);

  const [showAllChats, setShowAllChats] = useState(false);
  const maxVisibleChats = 4;
  const hasMoreChats = filteredChats?.length > maxVisibleChats;
  const displayedChats = showAllChats ? filteredChats : filteredChats?.slice(0, maxVisibleChats);

  return (
    <>
      <SearchModal toggleModal={toggleModal} isOpen={isOpen} onAskAi={handleAskAi} onSearchChange={onSearchChange}>
        <div className="grid gap-[8px] px-[8px] h-[439px] content-start custom-scrollbar">
          <>
            {(filteredTables?.length > 0 && query !== '') && (
              <SearchModalSection title={t('tables')} titleClass='!px-[8px]'>
              {displayedTables.map(table => (
                  <DropdownOptionItem key={table.uuid} icon={table?.metadata?.render_types?.[0]?.type === 'document' ? 'file' : 'table'} onClick={() => { router.push(`/${router.query.uuid}/t/${table.uuid}`); closeModal(); }} iconColor="custom-text-grey-200">
                    {table.name}
                  </DropdownOptionItem>
              ))}
              {hasMoreTables && (
                <p onClick={() => setShowAllTables(prev => !prev)} className="custom-text-grey-400 text-[12px] mt-[-12px] px-[16px] w-fit cursor-pointer">
                  {showAllTables ? t('showLess') : t('showMore', { count: filteredTables.length - maxVisibleTables })}
                </p>
              )}
                </SearchModalSection>
            )}
            <SearchModalSection title={t('actions')} titleClass='!px-[8px]'>
              <div className="grid">
                {query !== '' && <DropdownOptionItem icon='sparkles' onClick={() => { handleAskAi(query); }}>{t('askAi')}{query !== '' && <> &quot;{query}&quot;</>}</DropdownOptionItem>}
                {visibleTypeActions.map(({ type, label, icon }) => (
                  <DropdownOptionItem key={type} icon={icon} onClick={() => { createTable(type, null, closeModal(), null, null, null, query !== '' ? query : null); }}>
                    {label}{query !== '' && type === 'document' && <> &quot;{query}&quot;</>}
                  </DropdownOptionItem>
                ))}
                <DropdownOptionItem icon='folder-closed'
                  onClick={() => { query ? createTable('folder', query, closeModal(), 'private') : toggleCreateFolder(); toggleModal(); }}>
                  {t('createFolder')} {query !== '' && <> &quot;{query}&quot;</>}
                </DropdownOptionItem>
              </div>
            </SearchModalSection>
            <SearchModalSection title={t('chats')} titleClass='!px-[8px]'>
              <div className="grid">
                {displayedChats?.map(chat => (
                  <DropdownOptionItem
                    key={chat.id}
                    icon="sparkles"
                    iconColor="custom-text-grey-200"
                    onClick={() => { router.push(`/${router.query.uuid}/chats/${chat.id}`); closeModal(); }}
                  >
                    {chat.title}
                  </DropdownOptionItem>
                ))}
                {hasMoreChats && (
                  <p onClick={() => setShowAllChats(prev => !prev)} className="custom-text-grey-400 text-[12px]  px-[16px] w-fit cursor-pointer">
                    {showAllChats ? t('showLess') : t('showMore', { count: filteredChats.length - maxVisibleChats })}
                  </p>
                )}
              </div>
            </SearchModalSection>
            <SearchModalSection title={t('navigations')} titleClass='!px-[8px]'>
              <div className="grid">
                {navigations.map(nav => (
                  <DropdownOptionItem icon="arrow-right" onClick={() => { router.push(nav.url); closeModal(); }} iconColor="custom-text-grey-200" key={nav.name}>{nav.name}</DropdownOptionItem>
                ))}
              </div>
            </SearchModalSection>
          </>
        </div>
      </SearchModal>
      <CreateFolderModal onCreate={onCreateFolder} isModalOpen={isCreateFolderOpen} toggleModal={toggleCreateFolder} />
    </>
  );
}
export default GeneralSearchModal;